import { Alert } from '@cmsgov/ds-medicare-gov';
import React, { useState } from 'react';
import { allow } from '../../common/classes/allow';
import ApiErrorAlert from '../../common/components/api.error.alert';
import EnterPasswordSection from '../../common/components/account-trouble/enter.password.section';
import UserNameSection from '../../common/components/account-trouble/user.name.section';
import capitalizeFirstLetter from '../../common/functions/capitalize.first.letter';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import TranslatedSpan from '../../common/components/translated.span';
import ValidationAlertMessage from '../../common/components/validation.alert.message';
import ValidationAlerts from '../../common/components/validation.alerts';
import getUrlParameters from '../../common/functions/get.url.parameters';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart } from '../../common/hooks/use.analytics';
import useLoginApi from '../../common/hooks/use.login.api';
import { usePageTitle } from '../../common/hooks/use.page.title';
import useTrait from '../../common/hooks/use.trait';
import the from '../../common/objects/the';
import use from '../../common/objects/use';
import CreateAccountLinkSection from './create.account.link.section';
import LoginButtonSection from './login.button.section';
import TroubleLoggingSection from './trouble.logging.section';
import UpdateLogin from './update.login';
import useEventListener from '@use-it/event-listener';
import getClientId from '../../common/functions/get.client.id';

export default function Form() {
    const [showPassword, setShowPassword] = useState(false);
    const [showUpdateAccountPage, setShowUpdateAccountPage] = useState(false);
    const [showUsername, setShowUsername] = useState(false);
    const [showSecretQuestion, setShowSecretQuestion] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const [usernameRef, setUsernameRef] = useState();
    const [passwordRef, setPasswordRef] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const apiErrorMessage = useTrait(null);
    const nestedErrorMessage = useTrait(null);
    const deactiveAccount = useTrait(false);
    const validateEntireForm = useTrait(false);
    const counter = useTrait(0);
    const usernameError = useTrait(false);
    const passwordError = useTrait(false);
    const password = useTrait('');
    const username = useTrait('');
    const loginApi = useLoginApi();
   
    const formStart = useFormStart('login');
    const conversion = useConversion('login', 'login');
    usePageTitle(translate('Log in'));
   
    const getAccountIsUnlockedAlert = () => {
        if (!use.global.showAccountIsUnlockedAlert.get())
            return null;
        return <>
            <Div isRow={true}>
                <Div cols={11} colsSM={10} colsMD={9} colsLG={8} colsXL={7}>
                    <Alert
                        hideIcon={true}
                        variation={'success'}
                    >
                        <TranslatedSpan
                            english={'Your account is unlocked!'}
                            style={{fontWeight: 500}}
                        />
                        <p>
                            <TranslatedSpan english={'You\'ll also get a confirmation letter in the mail within the next 10-14 days to the address listed in your account.'}/>
                        </p>
                    </Alert>
                </Div>
            </Div>
        </>;
    };
   
    const getApiErrorMessage = () => {
        if (!apiErrorMessage.get())
            return null;
        return translate(apiErrorMessage.get());
    };
   
    const getNestedErrorMessage = () => {
        if (!nestedErrorMessage.get())
            return null;
        return translate(nestedErrorMessage.get());
    };
   
    const getNewAccountLoginAlert = () => {
        if (!use.global.showNewAccountLoginAlert.get())
            return null;
        return <>
            <Div isRow={true}>
                <Div cols={11} colsSM={10} colsMD={9} colsLG={8} colsXL={7}>
                    <Alert
                        hideIcon={true}
                        variation={'success'}
                    >
                        <TranslatedSpan
                            english={'You\'re all set!'}
                            style={{fontWeight: 500}}
                        />
                        <p>
                            <TranslatedSpan english={'You can now log in and start using your account.  You\'ll also get a confirmation letter in the mail within the next 10-14 days to the address listed in your account.'}/>
                        </p>
                    </Alert>
                </Div>
            </Div>
        </>;
    };
   
    const getValidationMessages = () => {
        const messages = [];
        if (!validateEntireForm.get())
            return messages;
        if (!validateUsername()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('Please enter a username.')}
                    fieldReference={usernameRef}
                    linkText={translate('Username')}
                />
            );
        }
        if (!validatePassword()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('Please enter a password.')}
                    fieldReference={passwordRef}
                    linkText={translate('Password')}
                />
            );
        }
        return messages;
    };
   
    // Show error message based on Error_code
    const handleErrorCode = (error_code, fieldsToUpdate = []) => {
        switch (error_code) {
                case 'SSO-0':
                    reset();
                    apiErrorMessage.set('Please enter username and password.');
                    break;
                case 'SSO-1':
                    //temporary lockout
                    reset();
                    apiErrorMessage.set('Your account is locked to protect your information. You can try to log in again in 1 hour.');
                    break;
                case 'SSO-2':
                    reset();
                    apiErrorMessage.set('Your account is no longer active. To re-open your account,');
                    deactiveAccount.set(true);
                    break;
                case 'SSO-3':
                    reset();
                    const count = counter.set(counter.get() + 1);
                    if (count <= 3) {
                        apiErrorMessage.set('Your username or password doesn\'t match our records. Check the information you entered and try again.');
                    } else if (count === 4) {
                        apiErrorMessage.set('Your username and password doesn\'t match our records. You have 1 more try to re-enter your username and password. If it doesn\'t match our records, your accout will be locked.');
                    } else {
                        apiErrorMessage.set('Your account is locked to protect your information. You can try to log in again in 1 hour.');
                    }
                    break;
                case 'SSO-4':
                case 'SSO-6':
                    // needs to update password
                    setShowPassword(true);
                    setShowUpdateAccountPage(true);
                    break;
                case 'SSO-7':
                    reset();
                    apiErrorMessage.set('Your username or password doesn\'t match our records. Check the information you entered and try again.');
                    break;
                case 'SSO-12':
                    //permanent lockout
                    reset();
                    apiErrorMessage.set('Your account was locked to protect your information.');
                    capitalizeFirstLetter(translate('Unlock My Account').toLowerCase());
                    nestedErrorMessage.set('Unlock My Account');
                    break;
                case 'SSO-13':
                    //needs to update username, password, security question oe any combinations of these
                    if (fieldsToUpdate.indexOf("username") >= 0)
                        setShowUsername(true);
                    if (fieldsToUpdate.indexOf("password") >= 0)
                        setShowPassword(true);
                    if (fieldsToUpdate.indexOf("question") >= 0)
                        setShowSecretQuestion(true);
                    if (fieldsToUpdate.indexOf("email") >= 0)
                        setShowEmail(true);
            
                    setShowUpdateAccountPage(true);
                    break;
                default:
                    reset();
                    apiErrorMessage.set(`We can't process your request at this time. We're sorry for any inconvenience. Please try again.`);
                    break;
        }
    };
   
    const onLogin = () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
   
        ssoLogin({
            username,
            password
        });
    };
   
    const reset = () => {
        apiErrorMessage.set(null);
        nestedErrorMessage.set(null);
        deactiveAccount.set(false);
    };
   
    const ssoLogin = async ({username, password}) => {
        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef !== null) {
            errorFieldRef.focus();
            setIsLoading(false);
            return;
        }
        const headers = {
            headers: the.defaultHeader,
        };
        const onSuccess = () => conversion.success();
        try {
            loginApi.login(username.get(), password.get(), getClientId(), getUrlParameters(), headers, onSuccess, result => {
                setIsLoading(false);
                if (!result?.response?.data) {
                    conversion.failAPI('unknown error');
                    return;
                }
                use.global.showAccountIsUnlockedAlert.set(false);
                use.global.showNewAccountLoginAlert.set(false);
            
                const errorCode = result.response.data.code;
                errorCode === 'SSO-13' ?
                    handleErrorCode(errorCode, result.response.data['mustUpdate']) :
                    handleErrorCode(errorCode, null);
                const nestedMessage = getNestedErrorMessage() === null ? getApiErrorMessage() : getApiErrorMessage().concat(getNestedErrorMessage());
                const errorMessage = nestedMessage || 'unknown error';
                conversion.failAPIIntermediate(errorMessage, errorCode);
                conversion.failAPI(errorMessage, errorCode);
            });
        } catch (e) {
            handleErrorCode(e, null);
            conversion.failAPI('unknown error');
            setIsLoading(false);
        }
    };
   
    const updatePassword = (value = '') => {
        allow.aString(value);
        formStart();
        const newValue = value.trimStart();
        password.set(newValue);
        validatePassword();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateUsername = (value = '') => {
        allow.aString(value);
        formStart();
        const newValue = value.trimStart();
        username.set(newValue);
        validateUsername();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const validateForm = () => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (!validateUsername())
            errorFieldRef = usernameRef;
        if (!validatePassword() && !errorFieldRef)
            errorFieldRef = passwordRef;
        const messages = getValidationMessages();
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidation(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };
   
    const validatePassword = () => {
        const isValid = password.get() !== '';
        if (passwordError.get() === isValid)
            passwordError.set(!isValid);
        return isValid;
    };
   
    const validateUsername = () => {
        const isValid = username.get() !== '';
        if (usernameError.get() === isValid)
            usernameError.set(!isValid);
        return isValid;
    };
   
    useEventListener('keyup', event => {
        if (event.key === 'Enter')
            onLogin();
    });
   
    return <>
        {!showUpdateAccountPage &&
      <Div
          isContainer={true}
          padding={3}
      >
          <fieldset style={{
              border: 0,
              margin: 0,
              padding: 0,
          }}>
              <legend>
                  <Div isRow={true}>
                      <Div cols={12}>
                          <H1>
                              <TranslatedSpan
                                  english={'Log in'}
                                  style={{whiteSpace: 'nowrap'}}
                              />
                          </H1>
                      </Div>
                  </Div>
              </legend>
      
              <Div>
                  <CreateAccountLinkSection/>
              </Div>
      
              {getAccountIsUnlockedAlert()}
              {getNewAccountLoginAlert()}
      
              <ValidationAlerts
                  heading={`${translate('Error')}: ${translate('Complete these items to log in.')}`}
                  validationMessages={getValidationMessages()}
              />
      
              <ApiErrorAlert
                  deactiveAccount={deactiveAccount.get()}
                  errorMessage={getApiErrorMessage()}
                  nestedErrorMessage={getNestedErrorMessage()}
              />
      
              <UserNameSection
                  errorMessage={usernameError.get() ? `${translate('Error')}: ${translate('Enter your username')}` : null}
                  inputRef={setUsernameRef}
                  label={translate('Username')}
                  onBlur={event => updateUsername(event.currentTarget.value)}
                  onChange={event => updateUsername(event.currentTarget.value)}
                  onFocus={formStart}
                  value={username.get()}
              />
              <EnterPasswordSection
                  errorMessage={passwordError.get() ? `${translate('Error')}: ${translate('Enter your password')}` : null}
                  id={'password-textbox'}
                  inputRef={setPasswordRef}
                  label={translate('Password')}
                  onBlur={event => updatePassword(event.currentTarget.value)}
                  onChange={event => updatePassword(event.currentTarget.value)}
                  onFocus={formStart}
                  showForgotPasswordLink={true}
                  value={password.get()}
              />
          </fieldset>
          <LoginButtonSection onClick={onLogin} isLoading={isLoading}/>
          <TroubleLoggingSection/>
      </Div>}
   
        {showUpdateAccountPage &&
      <UpdateLogin
          showUsername={showUsername}
          showPassword={showPassword}
          showSecretQuestion={showSecretQuestion}
          showEmail={showEmail}
          username={username.get()}
          password={password.get()}
          backToLogin={() => {
              setShowUpdateAccountPage(false);
              apiErrorMessage.set(null);
              deactiveAccount.set(false);
              nestedErrorMessage.set(null);
              username.set('');
              password.set('');
          }}
      />}
    </>;
}
